import InputGroupContent from '@/components/elements/input'
import SpinnerLoading from '@/components/elements/spinner'
import Submit from '@/components/elements/submit'
import Toast from '@/components/elements/toast'
import Layout from '@/components/layout/layout'
import Icon from '@/components/shared/icons'
import { useImageUploadToast } from '@/hooks/useImageUploadToast'
import { useUploader } from '@/hooks/useUploader'
import useUploadProgress from '@/hooks/useUploadProgress'
import { changePassword, sendVerifyCode } from '@/services/api/auth/auth'
import { fileUpdateById } from '@/services/api/files/files'
import { API_URL } from '@/services/api/keys'
import { userUpdateById } from '@/services/api/users/users'
import { updateSession } from '@/services/session/session'
import { updateUserData } from '@/store/features/auth'
import {
	imageRefrestUpdate,
	sessionRefrestUpdate,
} from '@/store/features/state'
import { AppDispatch, RootState } from '@/store/store'
import JwtPayload from '@/entities/api/jwt'
import PasswordEntity from '@/entities/api/password'
import passwordSchema from '@/entities/schemas/password'
import { userUpdateSchema } from '@/entities/schemas/users'
import moduleConvert from '@/utils/module-convert'
import { roleConvert } from '@/utils/role-convert'
import {
	Badge,
	Box,
	Button,
	Flex,
	Grid,
	Heading,
	Image,
	SimpleGrid,
	Span,
	Text,
	useBreakpointValue,
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { InferType } from 'yup'

type UserFormValues = InferType<typeof userUpdateSchema>

const Account = ({ data }: { data: JwtPayload }) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<UserFormValues>({
		resolver: yupResolver(userUpdateSchema),
	})
	const {
		register: registerPassword,
		handleSubmit: handleSubmitPassword,
		formState: { errors: errorsPassword },
	} = useForm<PasswordEntity>({
		resolver: yupResolver(passwordSchema),
	})
	const token = useSelector((state: RootState) => state.auth.token)
	const { imageRefrest } = useSelector((state: RootState) => state.state)
	const passwordFormRef = useRef<HTMLFormElement>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [isUpdateImage, setIsUpdateImage] = useState<boolean>(false)
	const [isUploadImage, setIsUploadImage] = useState<boolean>(false)
	const dispatch = useDispatch<AppDispatch>()
	const [isChangePassword, setIsChangePassword] = useState<boolean>(false)
	const { inputRef, files, isProcessing, handleFileChange, clearFiles } =
		useUploader()
	const { handleProgress } = useImageUploadToast()
	const { executeWithProgress, progress, isLoadingProgress } =
		useUploadProgress(async () => {
			try {
				if (!data?.image?._id) {
					Toast({
						type: 'error',
						message: 'El usuario no tiene id',
					})
					return
				}

				const first = files[0]
				if (!first) {
					Toast({ type: 'error', message: 'No hay archivos para subir' })
					return
				}

				const fileToUpload = first.file
				const response = await fileUpdateById(
					token ?? '',
					data.image!._id ?? '',
					fileToUpload
				)

				if (
					response?.status === 500 ||
					response?.status === 404 ||
					response?.status === 400
				) {
					Toast({
						type: 'error',
						message: 'La imagen no se puede actualizar.',
					})
					return
				}

				if (response?.status === 401) {
					dispatch(sessionRefrestUpdate())
					return
				}

				dispatch(imageRefrestUpdate())
				setIsUpdateImage(false)
				setIsUploadImage(false)
				clearFiles()
			} catch (error) {
				console.log(error)
				Toast({
					type: 'error',
					message: 'Algo salio mal al actualizar al usuario',
				})
			}
		})

	const onSubmitUpdate = async (dataYup: UserFormValues) => {
		try {
			setIsLoading(true)

			if (!dataYup?.name || !dataYup?.lastname) {
				Toast({ type: 'error', message: 'No hay data' })
				return
			}

			const response = await userUpdateById(token ?? '', data.sub, {
				name: dataYup.name !== data.name ? dataYup.name : data.name,
				lastname:
					dataYup.lastname !== data.lastname ? dataYup.lastname : data.lastname,
			})

			if (response?.status === 500 || response?.status === 404) {
				Toast({
					type: 'error',
					message: 'Algo salio mal al actualizar al usuario',
				})
				return
			}

			if (response?.status === 401) {
				Toast({
					type: 'info',
					message: 'La session del usuario a expirado',
				})
				dispatch(sessionRefrestUpdate())
				return
			}

			if (response?.status === 304) {
				Toast({
					type: 'error',
					message: 'No se permite modificar ciertos campos',
				})
				return
			}

			if (response?.status === 409) {
				Toast({
					type: 'error',
					message:
						'El correo o el telefono nuevos, ya existen en otro usuario.',
				})
				return
			}

			dispatch(
				updateUserData({
					...data,
					name: dataYup.name ? dataYup.name : data.name,
					lastname: dataYup.lastname ? dataYup.lastname : data.lastname,
				})
			)
			updateSession({
				name: dataYup.name ? dataYup.name : data.name,
				lastname: dataYup.lastname ? dataYup.lastname : data.lastname,
			})
			Toast({ type: 'success', message: 'Usuario actualizado correctamente' })
			setIsLoading(false)
		} catch (error) {
			setIsLoading(false)
			Toast({
				type: 'error',
				message: 'Algo salio mal al actualizar al usuario',
			})
		}
	}

	const onSubmitPassword = async (dataPassword: PasswordEntity) => {
		try {
			setIsLoading(true)

			console.log('hola')

			const response = await changePassword(
				dataPassword.code,
				data?.email,
				dataPassword.passwordCurrent,
				dataPassword.passwordNew
			)

			if (response?.status === 500 || response?.status === 404) {
				Toast({
					type: 'error',
					message: 'Algo salio mal y no se pudo cambiar la contraseña',
				})
				return
			}

			if (response?.status === 401) {
				Toast({
					type: 'info',
					message: 'La session del usuario a expirado',
				})
				dispatch(sessionRefrestUpdate())
				return
			}

			Toast({
				type: 'success',
				message: 'Se cambio la contraseña exitosamente',
			})
			setIsChangePassword(false)
			if (passwordFormRef.current) {
				passwordFormRef.current?.reset()
			}
			setIsLoading(false)
		} catch (error) {
			setIsLoading(false)
			Toast({
				type: 'error',
				message: 'Algo salio mal al cambiar la contraseña',
			})
		}
	}

	const sendVerifyCodeFn = async () => {
		try {
			const response = await sendVerifyCode(data?.email ?? '')

			if (response?.status === 500 || response?.status === 404) {
				Toast({
					type: 'error',
					message: 'No se puedo enviar el codigo',
				})
				return
			}
			Toast({
				type: 'success',
				message: 'Se envio un codigo de confirmacion a tu correo',
			})
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Hubo un problema al enviar el codigo de confirmacion',
			})
		}
	}

	const changePasswordIu = async () => {
		sendVerifyCodeFn()
		setIsChangePassword(true)
		if (passwordFormRef.current) {
			passwordFormRef.current?.reset()
		}
	}

	const profileImageSize = useBreakpointValue({
		base: '150px',
		md: '180px',
		lg: '200px',
	})
	const profileImageContainerSize = useBreakpointValue({
		base: '170px',
		md: '200px',
		lg: '220px',
	})

	useEffect(() => {
		if (files.length > 0) {
			setIsUploadImage(true)
			Toast({
				type: 'success',
				duration: 1000,
				message: 'La imagen se cargo correctamente',
			})
		}
	}, [files])

	useEffect(() => {
		handleProgress(progress, isLoadingProgress)
	}, [progress, isLoadingProgress])

	return (
		<Layout data={data}>
			<Flex
				w='full'
				display='flex'
				h={{ base: 'auto', lg: '100vh' }}
				direction={{ base: 'column', lg: 'row' }}
				align='stretch'
			>
				<Box
					w={{ base: 'full', lg: '50%' }}
					h='full'
					p={{ base: '3', md: '4', lg: '6' }}
					flex='1'
					display='flex'
					flexDirection='column'
					alignItems='center'
					mb={{ base: '6', lg: '0' }}
				>
					<Box w='full' mb='2'>
						<Heading
							as='h3'
							color='gray.600'
							fontSize={{ base: 'md', md: 'lg' }}
							fontWeight='lg'
						>
							Perfil de usuario
						</Heading>
					</Box>
					<Box
						w='full'
						h='full'
						display='flex'
						flexDirection='column'
						alignItems='center'
						p={{ base: '2', md: '4' }}
						bg='white'
						borderTop='2px solid'
						borderBottom='2px solid'
					>
						<Box
							w={profileImageContainerSize}
							h={profileImageContainerSize}
							borderRadius='full'
							bg='black'
							display='flex'
							justifyContent='center'
							alignItems='center'
							mx='auto'
						>
							<Box
								w={profileImageSize}
								h={profileImageSize}
								borderRadius='full'
								bg='white'
								display='flex'
								justifyContent='center'
								alignItems='center'
								position='relative'
							>
								{data?.image?.url && (
									<Image
										key={imageRefrest}
										src={`${API_URL}/${data.image?.url}`}
										w='full'
										h='full'
										borderRadius='full'
										objectFit='cover'
										alt={`image - ${data?.name} ${data?.lastname} `}
									/>
								)}
								<Box
									w={{
										base: `${isUpdateImage ? '110px' : '25px'}`,
										md: `${isUpdateImage ? '120px' : '30px'}`,
									}}
									h={{ base: '25px', md: '30px' }}
									borderRadius='full'
									p={isUpdateImage ? 4 : 0}
									bg='white'
									position='absolute'
									bottom='-4'
									border='4px solid'
									display='flex'
									justifyContent={
										isUpdateImage
											? isUploadImage
												? 'center'
												: 'center'
											: 'center'
									}
									alignItems='center'
								>
									{!isUpdateImage && (
										<Icon
											click={() => setIsUpdateImage(true)}
											icon='camera'
											size='sm'
										/>
									)}
									{isUpdateImage && !isProcessing && !isUploadImage && (
										<Box w='auto' h='auto' position='relative'>
											<Badge
												minW='3'
												minH='3'
												position='absolute'
												right='-1.5'
												top='0'
												borderRadius='full'
												bg={isUploadImage ? 'green' : 'red'}
											/>
											<Box position='relative'>
												<input
													className='absolute inset-0 top-0 left-0 w-full h-full opacity-0 cursor-pointer'
													type='file'
													ref={inputRef}
													onChange={handleFileChange}
													accept='image/*'
												/>
												<Icon click={() => {}} icon='upload' size='lg' />
											</Box>
										</Box>
									)}
									{isProcessing && <SpinnerLoading />}
									{isUpdateImage && isUploadImage && (
										<Icon
											click={() => executeWithProgress()}
											icon='saved'
											color='black'
											size='lg'
										/>
									)}
								</Box>
							</Box>
						</Box>
						<Heading
							as='h2'
							mt='2'
							fontWeight='600'
							color='black'
							fontSize={{ base: 'md', md: 'lg' }}
							textAlign='center'
						>
							{data?.name} {data?.lastname}
						</Heading>
						<Text color='gray.600' fontWeight='md' textAlign='center'>
							{roleConvert(data?.position)}
						</Text>
					</Box>

					<Box
						w='full'
						mt={{ base: '4', md: '6' }}
						display='flex'
						justifyContent='space-between'
					>
						<Heading
							as='h3'
							color='gray.600'
							fontSize={{ base: 'md', md: 'lg' }}
							fontWeight='lg'
						>
							Datos
						</Heading>
						{!isChangePassword && (
							<Text cursor='pointer' onClick={() => changePasswordIu()}>
								Cambiar contraseña
							</Text>
						)}
					</Box>
					<Box
						h='full'
						w='full'
						p={{ base: '3', md: '4' }}
						mt={{ base: '2', md: '6' }}
						bg='white'
						borderTop='2px solid'
						borderBottom='2px solid'
					>
						{!isChangePassword ? (
							<form onSubmit={handleSubmit(onSubmitUpdate)}>
								<Box w='full'>
									<InputGroupContent
										label='Nombre'
										placeholder='Nombre del usuario'
										defaultValue={data?.name ? data.name : 'Sin nombre'}
										type='text'
										name='name'
										errors={errors}
										register={register}
									/>
								</Box>

								<Box w='full' mt='2'>
									<InputGroupContent
										label='Apellido'
										placeholder='Apellido del usuario'
										defaultValue={
											data?.lastname ? data.lastname : 'Sin apellido'
										}
										type='text'
										name='lastname'
										errors={errors}
										register={register}
									/>
								</Box>

								<Box w='full' mt='2'>
									<InputGroupContent
										label='Correo eletronico'
										placeholder='Apellid del usuario'
										defaultValue={data?.email ? data.email : 'Sin apellido'}
										type='email'
										name='email'
										readOnlyActive={true}
									/>
								</Box>

								<Box
									w='full'
									mt='2'
									display='flex'
									justifyContent='center'
									alignItems='center'
								>
									<Submit
										text={isLoading ? <SpinnerLoading /> : 'Editar informacion'}
									/>
								</Box>
							</form>
						) : (
							<form
								ref={passwordFormRef}
								onSubmit={handleSubmitPassword(onSubmitPassword)}
							>
								<Box mb={{ base: '3', md: '4' }} textAlign='center'>
									<Text>
										Se envio un codigo al correo{' '}
										<Span fontWeight='600'>
											{data?.email ?? 'El correo no existe'}
										</Span>
									</Text>
									<Text mb={{ base: '3', md: '4' }} mt='2'>
										¿No te llego?{' '}
										<Span
											onClick={() => sendVerifyCodeFn()}
											fontWeight='600'
											color='blue'
										>
											Volver a enviar
										</Span>
									</Text>
								</Box>
								<Box w='full'>
									<InputGroupContent
										key={`code-${isChangePassword}`}
										label='Codigo'
										placeholder='Introduce el codigo que te enviamos al correo'
										type='number'
										name='code'
										errors={errorsPassword}
										register={registerPassword}
									/>
								</Box>
								<Box w='full'>
									<InputGroupContent
										key={`passwordCurrent-${isChangePassword}`}
										label='Contraseña actual'
										placeholder='Introduce la contraseña actual'
										type='password'
										name='passwordCurrent'
										errors={errorsPassword}
										register={registerPassword}
									/>
								</Box>
								<Box w='full'>
									<InputGroupContent
										key={`passwordNew-${isChangePassword}`}
										label='Nueva contraseña'
										placeholder='Introduce la nueva contraseña'
										type='password'
										name='passwordNew'
										errors={errorsPassword}
										register={registerPassword}
									/>
								</Box>
								<Box w='full'>
									<InputGroupContent
										key={`passwordRepeat-${isChangePassword}`}
										label='Repite la contraseña'
										placeholder='Repite la nueva contraseña'
										type='password'
										name='passwordRepeat'
										errors={errorsPassword}
										register={registerPassword}
									/>
								</Box>

								<Box
									w='full'
									mt='2'
									display='flex'
									justifyContent='center'
									alignItems='center'
								>
									<Submit
										text={isLoading ? <SpinnerLoading /> : 'Cambiar contraseña'}
									/>
									<Button
										p='4'
										ml='4'
										bg='black'
										color='white'
										border='none'
										fontSize='lg'
										fontWeight='800'
										borderRadius='lg'
										type='submit'
										onClick={() => setIsChangePassword(false)}
									>
										Cancelar
									</Button>
								</Box>
							</form>
						)}
					</Box>
				</Box>
				<Box
					w={{ base: 'full', lg: '50%' }}
					h='full'
					flex='1'
					p={{ base: '3', md: '4', lg: '6' }}
					display='flex'
					flexDirection='column'
				>
					<Heading
						as='h3'
						color='gray.600'
						fontSize={{ base: 'md', md: 'lg' }}
						fontWeight='lg'
					>
						Permisos
					</Heading>

					<Box
						h='full'
						mt='2'
						borderTop='2px solid'
						borderBottom='2px solid'
						p={{ base: '2', md: '3' }}
						w='full'
						bg='white'
					>
						<SimpleGrid
							columns={{ base: 1, sm: 2, lg: 2 }}
							gap={{ base: '2', md: '3' }}
							px={{ base: '1', md: '2' }}
						>
							{data?.permissions?.map((permission, index) => (
								<Box
									key={index}
									borderRadius='md'
									borderWidth='1px'
									borderColor='gray.200'
									p={{ base: '2', md: '3' }}
									boxShadow='sm'
									bg='white'
									transition='all 0.2s'
									_hover={{ boxShadow: 'md', borderColor: 'gray.400' }}
								>
									<Flex
										justifyContent='space-between'
										alignItems='center'
										mb='2'
										pb='1'
										borderBottom='1px'
										borderColor='gray.100'
									>
										<Text
											fontWeight='semibold'
											fontSize={{ base: 'sm', md: 'md' }}
											color='gray.700'
										>
											{moduleConvert(permission?.module ?? 'Without module')}
										</Text>
									</Flex>

									<Grid
										templateColumns='repeat(2, 1fr)'
										gap={{ base: '1', md: '2' }}
									>
										<Flex
											alignItems='center'
											py='1'
											px={{ base: '1', md: '2' }}
											borderRadius='md'
											bg={permission.add ? 'gray.50' : 'white'}
										>
											<Box mr='1'>
												<Icon
													icon={permission.add ? 'check' : 'close'}
													color={permission.add ? 'green' : 'red'}
													size='sm'
												/>
											</Box>
											<Text
												fontSize={{ base: 'xs', md: 'xs' }}
												fontWeight={permission.add ? 'medium' : 'normal'}
												color={permission.add ? 'gray.700' : 'gray.500'}
											>
												Añadir
											</Text>
										</Flex>

										<Flex
											alignItems='center'
											py='1'
											px={{ base: '1', md: '2' }}
											borderRadius='md'
											bg={permission.view ? 'gray.50' : 'white'}
										>
											<Box mr='1'>
												<Icon
													icon={permission.view ? 'check' : 'close'}
													color={permission.view ? 'green' : 'red'}
													size='sm'
												/>
											</Box>
											<Text
												fontSize={{ base: 'xs', md: 'xs' }}
												fontWeight={permission.view ? 'medium' : 'normal'}
												color={permission.view ? 'gray.700' : 'gray.500'}
											>
												Ver
											</Text>
										</Flex>

										<Flex
											alignItems='center'
											py='1'
											px={{ base: '1', md: '2' }}
											borderRadius='md'
											bg={permission.edit ? 'gray.50' : 'white'}
										>
											<Box mr='1'>
												<Icon
													icon={permission.edit ? 'check' : 'close'}
													color={permission.edit ? 'green' : 'red'}
													size='sm'
												/>
											</Box>
											<Text
												fontSize={{ base: 'xs', md: 'xs' }}
												fontWeight={permission.edit ? 'medium' : 'normal'}
												color={permission.edit ? 'gray.700' : 'gray.500'}
											>
												Editar
											</Text>
										</Flex>

										<Flex
											alignItems='center'
											py='1'
											px={{ base: '1', md: '2' }}
											borderRadius='md'
											bg={permission.remove ? 'gray.50' : 'white'}
										>
											<Box mr='1'>
												<Icon
													icon={permission.remove ? 'check' : 'close'}
													color={permission.remove ? 'green' : 'red'}
													size='sm'
												/>
											</Box>
											<Text
												fontSize={{ base: 'xs', md: 'xs' }}
												fontWeight={permission.remove ? 'medium' : 'normal'}
												color={permission.remove ? 'gray.700' : 'gray.500'}
											>
												Eliminar
											</Text>
										</Flex>
									</Grid>
								</Box>
							))}
						</SimpleGrid>
					</Box>
				</Box>
			</Flex>
		</Layout>
	)
}

export default Account
